import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import images from "../../../../utilities/images/images";
import { goBackToAdaptiveNextFocusSet } from "../../../../custom_utilities/dashboardBannerNavigation";
import { postAdaptiveLearningSummaryRequest } from "../../../../redux/adaptive_qbank/adaptiveQBankSlice";
import "./AdaptiveLearningSummary.css";

const pickValue = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return null;
};

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.list)) return value.list;
  if (Array.isArray(value?.result)) return value.result;
  return [];
};

const toSafeNumber = (value, fallback = 0) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const numeric = Number(String(value ?? "").replace("%", "").trim());
  return Number.isFinite(numeric) ? numeric : fallback;
};

const LearningSummarySkeleton = () => (
  <div
    className="aqb-learning-summary-skeleton"
    aria-busy="true"
    aria-label="Loading learning insights"
  >
    <section className="aqb-learning-summary-card">
      <h3>Learning Summary</h3>
      <div className="aqb-learning-summary-stats">
        <div className="aqb-learning-summary-main-stat">
          <div className="aqb-learning-summary-main-stat-row">
            <span className="aqb-ls-skel aqb-ls-skel-icon" />
            <div className="aqb-learning-summary-main-text">
              <span className="aqb-ls-skel aqb-ls-skel-value" />
              <span className="aqb-ls-skel aqb-ls-skel-caption" />
            </div>
          </div>
        </div>
        {Array.from({ length: 3 }, (_, index) => (
          <div className="aqb-learning-summary-mini-stat" key={`stat-skel-${index}`}>
            <span className="aqb-ls-skel aqb-ls-skel-mini-value" />
            <span className="aqb-ls-skel aqb-ls-skel-caption" />
          </div>
        ))}
      </div>
    </section>

    <section className="aqb-learning-summary-performance">
      <h4>Subtopic Performance</h4>
      <div className="aqb-learning-summary-performance-grid">
        {Array.from({ length: 4 }, (_, index) => (
          <div
            className="aqb-learning-summary-topic-card aqb-learning-summary-topic-card--skeleton"
            key={`topic-skel-${index}`}
          >
            <div className="aqb-learning-summary-topic-head">
              <span className="aqb-ls-skel aqb-ls-skel-title" />
              <span className="aqb-ls-skel aqb-ls-skel-badge" />
            </div>
            <div className="aqb-learning-summary-topic-meta">
              <span className="aqb-ls-skel aqb-ls-skel-meta" />
              <span className="aqb-ls-skel aqb-ls-skel-meta-value" />
            </div>
            <div className="aqb-ls-skel aqb-ls-skel-bar" />
          </div>
        ))}
      </div>
    </section>
  </div>
);

const AdaptiveLearningSummary = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const routeState = location?.state || {};
  const topic = routeState?.topic || {};
  const topicId = Number(routeState?.topic_id || topic?.topic_id || 0) || null;
  const subject = routeState?.subject || topic?.subject || topic?.subject_name || topic?.sub_name || "";
  const learningCycle = Number(routeState?.learning_cycle || 1) || 1;

  const { learningSummary, loading } = useSelector((state) => state.adaptiveQBank);

  const handleBackToNextFocusSet = () => {
    goBackToAdaptiveNextFocusSet(history, location, {
      topic,
    });
  };

  useEffect(() => {
    if (!topicId || !subject) return;
    dispatch(
      postAdaptiveLearningSummaryRequest({
        subject,
        topic_id: topicId,
        learning_cycle: learningCycle,
      })
    );
  }, [dispatch, learningCycle, subject, topicId]);

  const summaryRoot = useMemo(
    () => learningSummary?.data ?? learningSummary ?? {},
    [learningSummary]
  );
  const summaryMetrics = useMemo(
    () =>
      pickValue(
        summaryRoot?.learning_summary,
        summaryRoot?.learningSummary,
        summaryRoot?.summary,
        summaryRoot
      ) || {},
    [summaryRoot]
  );
  const topicNameLabel = pickValue(summaryRoot?.topic_name, topic?.topic_name, null);
  /** API uses `cycle_focus_label` in some envs, `focus_set_title` in others (e.g. completed set). */
  const cycleOrFocusLabel = pickValue(
    summaryRoot?.cycle_focus_label,
    summaryRoot?.focus_set_title,
    null
  );

  const overallAccuracy = toSafeNumber(
    pickValue(
      summaryMetrics?.overall_accuracy,
      summaryMetrics?.overallAccuracy,
      summaryMetrics?.accuracy_percent,
      summaryMetrics?.accuracyPercentage,
      summaryMetrics?.accuracy,
      summaryMetrics?.accuraccy_percent,
      0
    )
  );
  const correctCount = toSafeNumber(
    pickValue(
      summaryMetrics?.correct,
      summaryMetrics?.correct_count,
      summaryMetrics?.correctCount,
      0
    )
  );
  const incorrectCount = toSafeNumber(
    pickValue(
      summaryMetrics?.incorrect,
      summaryMetrics?.incorrect_count,
      summaryMetrics?.incorrectCount,
      0
    )
  );
  const totalQuestions = toSafeNumber(
    pickValue(
      summaryMetrics?.total_questions,
      summaryMetrics?.total_question,
      summaryMetrics?.total,
      summaryMetrics?.totalCount,
      0
    )
  );

  const subtopicPerformance = useMemo(
    () =>
      toArray(
        pickValue(
          summaryRoot?.subtopic_performance,
          summaryRoot?.subtopicPerformance,
          summaryMetrics?.subtopic_performance,
          summaryMetrics?.subtopicPerformance,
          summaryMetrics?.sub_topics,
          summaryMetrics?.subtopics,
          summaryRoot?.sub_topics,
          summaryRoot?.subtopics,
          summaryRoot?.topic_breakdown
        )
      ).map((item, index) => {
        const name =
          item?.subtopic_name ||
          item?.sub_topic_name ||
          item?.name ||
          `Subtopic ${index + 1}`;
        const accuracy = toSafeNumber(
          pickValue(
            item?.accuracy,
            item?.accuracy_percent,
            item?.accuracy_percentage,
            item?.percentage,
            0
          )
        );
        const status =
          item?.status ||
          item?.label ||
          (accuracy >= 70 ? "Strong Area" : "Needs Practice");
        return {
          name,
          accuracy: Number.isFinite(accuracy) ? Math.max(0, Math.min(accuracy, 100)) : 0,
          status,
        };
      }),
    [summaryRoot]
  );

  return (
    <div className="aqb-learning-summary-page">
      <header className="aqb-learning-summary-header">
        <button
          type="button"
          className="aqb-learning-summary-back"
          onClick={handleBackToNextFocusSet}
          aria-label="Go back"
        >
          <img src={images.new_backarrow} alt="" />
        </button>
        <div className="aqb-learning-summary-header-titles">
          {topicNameLabel ? (
            <h1>{topicNameLabel}</h1>
          ) : (
            <span className="aqb-ls-skel aqb-ls-skel-heading" />
          )}
          {loading?.learningSummary ? (
            <span className="aqb-ls-skel aqb-ls-skel-subheading" />
          ) : cycleOrFocusLabel ? (
            <h2>{cycleOrFocusLabel}</h2>
          ) : null}
        </div>
      </header>

      {loading?.learningSummary ? (
        <LearningSummarySkeleton />
      ) : (
      <>
      <section className="aqb-learning-summary-card">
        <h3>Learning Summary</h3>
          <div className="aqb-learning-summary-stats">
            <div className="aqb-learning-summary-main-stat">
              <div className="aqb-learning-summary-main-stat-row">
                <span className="aqb-learning-summary-target-icon-wrap">
                  <img src={images.target_icon} alt="" aria-hidden="true" />
                </span>
                <div className="aqb-learning-summary-main-text">
                  <span className="aqb-learning-summary-main-value">{`${Math.round(overallAccuracy)}%`}</span>
                  <span className="aqb-learning-summary-main-label">Overall Accuracy</span>
                </div>
              </div>
            </div>
            <div className="aqb-learning-summary-mini-stat">
              <span className="aqb-learning-summary-mini-value success">{correctCount}</span>
              <span className="aqb-learning-summary-mini-label">Correct</span>
            </div>
            <div className="aqb-learning-summary-mini-stat">
              <span className="aqb-learning-summary-mini-value danger">{incorrectCount}</span>
              <span className="aqb-learning-summary-mini-label">Incorrect</span>
            </div>
            <div className="aqb-learning-summary-mini-stat">
              <span className="aqb-learning-summary-mini-value">{totalQuestions}</span>
              <span className="aqb-learning-summary-mini-label">Total Q's</span>
            </div>
          </div>
      </section>

      <section className="aqb-learning-summary-performance">
        <h4>Subtopic Performance</h4>
        {subtopicPerformance.length > 0 ? (
          <div className="aqb-learning-summary-performance-grid">
            {subtopicPerformance.map((item) => {
              const normalizedStatus = String(item.status || "").toLowerCase();
              const isStrong =
                normalizedStatus.includes("strong") || normalizedStatus.includes("good");
              return (
                <div className="aqb-learning-summary-topic-card" key={`${item.name}-${item.accuracy}`}>
                  <div className="aqb-learning-summary-topic-head">
                    <h5>{item.name}</h5>
                    <span className={isStrong ? "success" : "warning"}>
                      {item.status}
                    </span>
                  </div>
                  <div className="aqb-learning-summary-topic-meta">
                    <p className="aqb-learning-summary-topic-label">Accuracy</p>
                    <p className="aqb-learning-summary-topic-label-value">{`${Math.round(item.accuracy)}%`}</p>
                  </div>
                  <div className="aqb-learning-summary-bar-wrap">
                    <div
                      className={`aqb-learning-summary-bar ${isStrong ? "success" : "warning"}`}
                      style={{ width: `${item.accuracy}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="aqb-learning-summary-empty">
            No learning summary data found for this cycle yet.
          </p>
        )}
      </section>
      </>
      )}
    </div>
  );
};

export default AdaptiveLearningSummary;
